import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { PiDotsThree } from 'react-icons/pi';
import { z } from 'zod';

import { DateTimePicker } from '@/components';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import AddButton from '@/components/Button/AddButton';
import { Autocomplete, Input, TextArea } from '@/components/Inputs';
import { Popup, PopupSection, type PopupSectionItem } from '@/components/Popup/Popup';
import { Tile } from '@/components/Tile/Tile';
import { useClickOutside } from '@/hooks/useClickOutside';

import { currentLocalTime, timeMaskProps } from '../../logbookTime';
import type { SafetyEquipmentOption } from '../hooks/useSafetyEquipment';
import type { SafetyItem } from '../types';

import s from './SafetyItemsSection.module.scss';

interface SafetyItemsSectionProps {
  items: SafetyItem[];
  editingId: string | null;
  addingNew: boolean;
  /** The vessel's Safety Equipment, which is what a checklist row names. */
  equipmentOptions: SafetyEquipmentOption[];
  /**
   * Whether the viewer may read and write the equipment inventory at all —
   * vessel gates the whole `/equipment` family on the one `Equipment` right, so
   * picking and adding stand or fall together. Without it the row falls back to
   * the free-text name it has always been, rather than an empty picker with no
   * way out of it.
   */
  canUseEquipment: boolean;
  onStartAdd(): void;
  onStartEdit(id: string): void;
  onCancel(): void;
  onSaveEdit(id: string, patch: Partial<SafetyItem>): void;
  onAddNew(values: Omit<SafetyItem, 'id'>): void;
  onRemove(id: string): void;
}

export function SafetyItemsSection({
  items,
  editingId,
  addingNew,
  equipmentOptions,
  canUseEquipment,
  onStartAdd,
  onStartEdit,
  onCancel,
  onSaveEdit,
  onAddNew,
  onRemove,
}: SafetyItemsSectionProps) {
  return (
    <Tile>
      <section className={s.section}>
        <h6 className={s.sectionTitle}>Safety Equipment Checklist</h6>

        {items.length === 0 && !addingNew && (
          <p className={s.empty}>Please add an item to get started.</p>
        )}

        {items.map((item, index) =>
          item.id === editingId ? (
            <SafetyItemEdit
              key={item.id}
              number={index + 1}
              item={item}
              equipmentOptions={equipmentOptions}
              canUseEquipment={canUseEquipment}
              onCancel={onCancel}
              onSubmit={(values) => onSaveEdit(item.id, values)}
            />
          ) : (
            <SafetyItemRow
              key={item.id}
              number={index + 1}
              item={item}
              onEdit={() => onStartEdit(item.id)}
              onDelete={() => onRemove(item.id)}
            />
          ),
        )}

        {addingNew ? (
          <SafetyItemEdit
            number={items.length + 1}
            equipmentOptions={equipmentOptions}
            canUseEquipment={canUseEquipment}
            onCancel={onCancel}
            onSubmit={(values) => onAddNew(values)}
          />
        ) : (
          <AddButton label="Item" onClick={onStartAdd} />
        )}
      </section>
    </Tile>
  );
}

interface SafetyItemRowProps {
  number: number;
  item: SafetyItem;
  onEdit(): void;
  onDelete(): void;
}

function SafetyItemRow({ number, item, onEdit, onDelete }: SafetyItemRowProps) {
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setOpened(false));

  const actionsList: PopupSectionItem[] = [
    { label: 'Edit', onClick: onEdit, icon: <BiPencil /> },
    { label: 'Delete', onClick: onDelete, icon: <BiTrash /> },
  ];

  return (
    <article className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>{item.name || `Item #${number}`}</p>
        {item.isNewEquipment && <span className={s.newBadge}>New equipment</span>}
        <button type="button" className={s.actions} onClick={() => setOpened((o) => !o)} ref={ref}>
          <PiDotsThree size={20} />
          {opened && (
            <Popup>
              <PopupSection items={actionsList} title="" onItemClick={() => setOpened(false)} />
            </Popup>
          )}
        </button>
      </div>
      <section className={s.inputs}>
        <Field label="Item" value={item.name} />
        <Field label="Time" value={item.time} />
        <Field label="Expiration" value={formatExpiration(item.expiration)} />
        <Field label="Remarks / Compliance" value={item.remarksCompliance} />
      </section>
    </article>
  );
}

interface SafetyItemEditProps {
  number: number;
  item?: SafetyItem;
  equipmentOptions: SafetyEquipmentOption[];
  canUseEquipment: boolean;
  onCancel(): void;
  onSubmit(values: Omit<SafetyItem, 'id'>): void;
}

const NAME_MAX = 150;
const REMARKS_MAX = 500;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const formatExpiration = (value?: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-GB');
};

const itemSchema = z.object({
  name: z
    .string()
    .min(1, 'Please pick a piece of safety equipment.')
    .max(NAME_MAX, `Max ${NAME_MAX} characters`),
  equipmentId: z.string(),
  isNewEquipment: z.boolean(),
  remarksCompliance: z.string().max(REMARKS_MAX, `Max ${REMARKS_MAX} characters`),
  time: z.string().refine((v) => !v || TIME_RE.test(v), 'Use HH:MM format (00:00–23:59)'),
  expiration: z.string(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

function SafetyItemEdit({
  number,
  item,
  equipmentOptions,
  canUseEquipment,
  onCancel,
  onSubmit,
}: SafetyItemEditProps) {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    mode: 'onChange',
    defaultValues: {
      name: item?.name ?? '',
      equipmentId: item?.equipmentId ?? '',
      isNewEquipment: item?.isNewEquipment ?? false,
      remarksCompliance: item?.remarksCompliance ?? '',
      time: item?.time ?? currentLocalTime(),
      expiration: item?.expiration ?? '',
    },
  });

  // The picker writes three fields at once, so what it wrote is read back rather
  // than held twice — `useWatch` rather than `watch` to keep the form's own
  // subscription out of render.
  const name = useWatch({ control, name: 'name' });
  const isNewEquipment = useWatch({ control, name: 'isNewEquipment' });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Item #{number}</p>
      </div>
      <section className={s.editInputs}>
        {canUseEquipment ? (
          <EquipmentPicker
            name={name}
            isNewEquipment={isNewEquipment}
            options={equipmentOptions}
            error={errors.name?.message}
            onPick={(picked) => {
              setValue('name', picked.name, { shouldValidate: true });
              setValue('equipmentId', picked.equipmentId);
              setValue('isNewEquipment', picked.isNewEquipment);
            }}
          />
        ) : (
          <Input
            label="Item"
            placeholder="Life jackets – 1 per person"
            error={errors.name?.message}
            {...register('name')}
          />
        )}
        <Input
          label="Time"
          placeholder="HH:MM"
          error={errors.time?.message}
          {...timeMaskProps(register('time'))}
        />
        <Controller
          control={control}
          name="expiration"
          render={({ field }) => (
            <DateTimePicker
              label="Expiration"
              placeholder="Jul 18, 2026"
              value={field.value}
              onChange={field.onChange}
              error={errors.expiration?.message}
              showTimeInput={false}
            />
          )}
        />
        <TextArea
          label="Remarks / Compliance"
          placeholder="Serviced and tagged, all in date."
          error={errors.remarksCompliance?.message}
          className={s.stretch}
          {...register('remarksCompliance')}
        />
      </section>
      <div className={s.groupItemActions}>
        <ButtonOutlinedSmall type="button" onClick={onCancel}>
          Cancel
        </ButtonOutlinedSmall>
        <ButtonSmall type="submit">{item ? 'Save' : 'Add'}</ButtonSmall>
      </div>
    </form>
  );
}

interface EquipmentPickerProps {
  name: string;
  isNewEquipment: boolean;
  options: SafetyEquipmentOption[];
  error?: string;
  onPick(picked: Pick<SafetyItem, 'equipmentId' | 'isNewEquipment' | 'name'>): void;
}

/**
 * What a checklist row is allowed to name: the vessel's Safety Equipment, plus
 * whatever the crew types that isn't in it yet. Adding is deferred to the log's
 * own save, so the control only marks the row — the inventory is untouched
 * until the entry is committed.
 */
function EquipmentPicker({ name, isNewEquipment, options, error, onPick }: EquipmentPickerProps) {
  const [search, setSearch] = useState('');

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? options.filter((o) => o.name.toLowerCase().includes(query)) : options;
  }, [options, search]);

  const pick = (picked: Pick<SafetyItem, 'equipmentId' | 'isNewEquipment' | 'name'>) => {
    onPick(picked);
    setSearch('');
  };

  return (
    <div className={s.picker}>
      <Autocomplete
        label="Item"
        mandatory
        placeholder={options.length ? 'Life raft' : 'No safety equipment yet'}
        error={error}
        value={search}
        onChange={setSearch}
        options={matches.map(({ id, name: optionName }) => ({ label: optionName, value: id }))}
        selectedOptionLabel={name || null}
        onItemSelect={(id) => {
          const picked = options.find((o) => o.id === id);
          pick(
            picked
              ? { name: picked.name, equipmentId: picked.id, isNewEquipment: false }
              : { name: '', equipmentId: '', isNewEquipment: false },
          );
        }}
        onNewOption={(typed) => {
          const trimmed = typed.trim();
          if (trimmed) pick({ name: trimmed, equipmentId: '', isNewEquipment: true });
        }}
      />
      {isNewEquipment && (
        <p className={s.pickerNote}>Saving the log adds it to Safety Equipment.</p>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  value?: string;
  stretch?: boolean;
}

function Field({ label, value, stretch }: FieldProps) {
  return (
    <article className={clsx(s.field, stretch && s.stretch)}>
      <span className={`body-medium ${s.fieldLabel}`}>{label}</span>
      <div className={s.fieldValue}>{value && value.length ? value : '-'}</div>
    </article>
  );
}
